import moment from "moment";
import "moment-timezone";
import flatDiagram from "./flatDiagram";
import { AutomationEventTypes } from "./AutomationModuleTypes";
import AutomationBlockTypes from "../AutomationBlockTypes";
import _ from "lodash";

const getTimezone = () => {
    return moment.tz.guess();
};

let instance = null;
export default class AutomationAttributesGenerator {
    constructor() {
        this.contexts = {
            CONTACT: "contact",
            CONVERSATION: "conversation",
            BLOCK: "block",
            EVENT: "event",
            WEBHOOK: "webhook",
            SYSTEM: "system",
            CUSTOM: "custom",
        };
        this.VALUE_TYPES = {
            STRING: "string",
            NUMBER: "number",
            BOOLEAN: "boolean",
            DATE: "date",
            DATETIME: "datetime",
            TIME: "time",
            ENUM: "enum",
            ARRAY: "array",
            ARRAY_NUMBER: "array_number",
            ARRAY_STRING: "array_string",
            ARRAY_DATE: "array_date",
            ARRAY_OBJECT: "array_objectId",
            ARRAY_BOOLEAN: "array_boolean",
            OBJECT: "object",
            MIXED: "mixed",
        };
        this.defaultOperators = {
            string: [
                "equal",
                "not_equal",
                "contains",
                "not_contains",
                "empty",
                "not_empty",
                "starts_with",
                "ends_with",
                "shorter_than",
                "longer_than",
            ],
            array: ["equal", "not_equal", "contains", "not_contains", "empty", "not_empty"],
            number: ["equal", "not_equal", "greater_than", "less_than", "greater_than_or_equal", "less_than_or_equal"],
            boolean: ["equal", "not_equal", "empty", "not_empty"],
            date: [
                "equal",
                "not_equal",
                "greater_than",
                "less_than",
                "greater_than_or_equal",
                "less_than_or_equal",
                "empty",
                "not_empty",
            ],
            ObjectId: ["equal", "not_equal", "empty", "not_empty"],
            object: ["equal", "not_equal", "empty", "not_empty"],
            mixed: [
                "equal",
                "not_equal",
                "contains",
                "not_contains",
                "empty",
                "not_empty",
                "starts_with",
                "ends_with",
                "shorter_than",
                "longer_than",
                "greater_than",
                "less_than",
                "greater_than_or_equal",
                "less_than_or_equal",
            ],
        };
    }

    /**
     *
     * @return {AutomationAttributesGenerator}
     */
    static create() {
        if (!instance) {
            instance = new AutomationAttributesGenerator();
        }
        return instance;
    }

    /**
     * @param automation
     * @param prefix
     * @param allowedContexts
     * @return {[{defaultFilterValue: string, availableOperators: string[], defaultOperator: string, name: string, placeholder: string, type: string, value: string}]}
     */
    list(automation, prefix = undefined, allowedContexts = []) {
        console.log("AutomationAttributesGenerator.list", { automation, prefix });
        allowedContexts = allowedContexts.length ? allowedContexts : Object.values(this.contexts);
        const contactAttributes = allowedContexts.includes(this.contexts.CONTACT)
            ? this.generateContactAttributes(prefix)
            : [];
        const conversationAttributes = allowedContexts.includes(this.contexts.CONVERSATION)
            ? this.generateConversationAttributes(prefix)
            : [];
        const automationBlocks = this.generateAutomationBlock(automation);
        const events = allowedContexts.includes(this.contexts.EVENT) ? this.generateEvents(automation) : [];
        const systemAttributes = allowedContexts.includes(this.contexts.SYSTEM)
            ? this.generateSystemAttributes(prefix)
            : [];
        const customAttributes = allowedContexts.includes(this.contexts.CUSTOM)
            ? this.generateCustomAttributes(prefix)
            : [];
        const webhookAttributes = allowedContexts.includes(this.contexts.WEBHOOK)
            ? this.generateWebhookAttributes(automation)
            : [];
        const result = [
            ...contactAttributes,
            ...conversationAttributes,
            ...automationBlocks,
            ...events,
            ...systemAttributes,
            ...customAttributes,
            ...webhookAttributes,
        ].flat();
        console.log("AutomationAttributesGenerator.list response", {
            all: result,
            block: automationBlocks,
            contact: contactAttributes,
            conversation: conversationAttributes,
            events,
            system: systemAttributes,
            custom: customAttributes,
            webhook: webhookAttributes,
        });
        return result;
    }

    generateAutomationBlock(automation) {
        const blocks = flatDiagram(automation.diagram);
        return blocks.map((block) => {
            const valueTypes = {
                [AutomationBlockTypes.LOOP_NEXT]: this.VALUE_TYPES.OBJECT,
                [AutomationBlockTypes.FIND_CONTACTS]: this.VALUE_TYPES.OBJECT,
                [AutomationBlockTypes.FIND_CONTACT]: this.VALUE_TYPES.OBJECT,
                [AutomationBlockTypes.WEBHOOK]: this.VALUE_TYPES.OBJECT,
                [AutomationBlockTypes.RECORDS_FROM_CSV]: this.VALUE_TYPES.OBJECT,
                [AutomationBlockTypes.MESSAGE_RECEIVED]: this.VALUE_TYPES.OBJECT,
                [AutomationBlockTypes.CREATE_CONTACT]: this.VALUE_TYPES.OBJECT,
            };
            const valueType = valueTypes[block.type] || this.VALUE_TYPES.STRING;
            const subAttributesFN = (block) => {
                return (
                    {
                        [AutomationBlockTypes.CREATE_CONTACT]: this.listBlockCreateContactFields(`block.${block.id}`),
                        [AutomationBlockTypes.LOOP_NEXT]: this.listBlockLoopFields(`block.${block.id}`),
                        [AutomationBlockTypes.FIND_CONTACTS]: this.listBlockFindContactsFields(`block.${block.id}`),
                        [AutomationBlockTypes.FIND_CONTACT]: this.listBlockFindContactFields(`block.${block.id}`),
                        [AutomationBlockTypes.WEBHOOK]: this.listBlockWebhookFields(`block.${block.id}`),
                        [AutomationBlockTypes.RECORDS_FROM_CSV]: this.listBlockRecordsFromCSVFields(
                            `block.${block.id}`,
                            block
                        ),
                        [AutomationBlockTypes.MESSAGE_RECEIVED]: this.listBlockMessageReceivedFields(
                            `block.${block.id}`,
                            block
                        ),
                    }[block.type] || undefined
                );
            };
            return this.generateAttribute(
                this.contexts.BLOCK,
                `block.${block.id}`,
                block.name || block.type,
                valueType,
                undefined,
                subAttributesFN(block)
            );
        });
    }

    listBlockLoopFields(prefix) {
        return [
            this.generateAttribute(
                this.contexts.BLOCK,
                `${prefix}.loop_data_key`,
                "loop_data_key",
                this.VALUE_TYPES.STRING
            ),
            this.generateAttribute(
                this.contexts.BLOCK,
                `${prefix}.loop_data_value`,
                "loop_data_value",
                this.VALUE_TYPES.MIXED
            ),
            this.generateAttribute(this.contexts.BLOCK, `${prefix}.loop_index`, "loop_index", this.VALUE_TYPES.NUMBER),
            this.generateAttribute(
                this.contexts.BLOCK,
                `${prefix}.loop_length`,
                "loop_length",
                this.VALUE_TYPES.NUMBER
            ),
            this.generateAttribute(this.contexts.BLOCK, `${prefix}.loop_id`, "loop_id", this.VALUE_TYPES.STRING),
        ];
    }

    listBlockCreateContactFields(prefix) {
        return [
            this.generateAttribute(
                this.contexts.BLOCK,
                `${prefix}.contact`,
                "CONTACT",
                this.VALUE_TYPES.OBJECT,
                undefined,
                [...this.generateContactAttributes(`${prefix}.contact`)]
            ),
        ];
    }

    listBlockWebhookFields(prefix) {
        return [
            this.generateAttribute(this.contexts.BLOCK, `${prefix}.success`, "success", this.VALUE_TYPES.BOOLEAN),
            this.generateAttribute(this.contexts.BLOCK, `${prefix}.statusCode`, "statusCode", this.VALUE_TYPES.NUMBER),
            this.generateAttribute(this.contexts.BLOCK, `${prefix}.data`, "data", this.VALUE_TYPES.OBJECT, undefined, [
                this.generateAttribute(
                    this.contexts.BLOCK,
                    `${prefix}.data.headers`,
                    "headers",
                    this.VALUE_TYPES.OBJECT
                ),
                this.generateAttribute(
                    this.contexts.BLOCK,
                    `${prefix}.data.bodyText`,
                    "bodyText",
                    this.VALUE_TYPES.STRING
                ),
                this.generateAttribute(this.contexts.BLOCK, `${prefix}.data.body`, "body", this.VALUE_TYPES.MIXED),
            ]),
        ];
    }

    listBlockRecordsFromCSVFields(prefix, block) {
        const fields = _.get(block, "payload.mapping", []);
        return [
            this.generateAttribute(
                this.contexts.BLOCK,
                `${prefix}.items`,
                "items",
                this.VALUE_TYPES.ARRAY_OBJECT,
                undefined,
                [
                    this.generateAttribute(
                        this.contexts.BLOCK,
                        `${prefix}.items.$..*`,
                        "*",
                        this.VALUE_TYPES.ARRAY_OBJECT
                    ),
                    ...fields.map((field, index) =>
                        this.generateAttribute(
                            this.contexts.BLOCK,
                            `${prefix}.items.$..${field.field}`,
                            field.field,
                            this.VALUE_TYPES.MIXED
                        )
                    ),
                ]
            ),
        ];
    }

    listBlockMessageReceivedFields(prefix, block) {
        return [
            this.generateAttribute(
                this.contexts.BLOCK,
                `${prefix}.message`,
                "Received Message",
                this.VALUE_TYPES.OBJECT,
                undefined,
                [...this.generateMessageAttributes(this.contexts.BLOCK, `${prefix}.message`)]
            ),
            this.generateAttribute(
                this.contexts.BLOCK,
                `${prefix}.email`,
                "Received Email",
                this.VALUE_TYPES.OBJECT,
                undefined,
                [...this.generateEmailAttributes(this.contexts.BLOCK, `${prefix}.email`)]
            ),
        ];
    }

    listBlockFindContactsFields(prefix) {
        return [
            this.generateAttribute(this.contexts.BLOCK, `${prefix}.count`, "count", this.VALUE_TYPES.NUMBER),
            this.generateAttribute(
                this.contexts.BLOCK,
                `${prefix}.items`,
                "items",
                this.VALUE_TYPES.ARRAY_OBJECT,
                undefined,
                [
                    this.generateAttribute(
                        this.contexts.BLOCK,
                        `${prefix}.items.$..*`,
                        "*",
                        this.VALUE_TYPES.ARRAY_OBJECT
                    ),
                    ...this.generateContactAttributes(`${prefix}.items.$.`),
                ]
            ),
        ];
    }

    listBlockFindContactFields(prefix) {
        return [
            this.generateAttribute(this.contexts.BLOCK, `${prefix}.$..*`, "*", this.VALUE_TYPES.OBJECT),

            ...this.generateContactAttributes(`${prefix}`),
        ];
    }

    generateCustomAttributes(prefix = "custom") {
        const context = this.contexts.CUSTOM;

        return [
            this.generateAttribute(context, `${prefix}.id1`, "Custom Attribute (String)", this.VALUE_TYPES.STRING),
            this.generateAttribute(context, `${prefix}.id2`, "Custom Attribute (Number)", this.VALUE_TYPES.NUMBER),
        ];
    }

    generateSystemAttributes(prefix = "system") {
        const context = this.contexts.SYSTEM;

        return [
            this.generateAttribute(context, `${prefix}.date`, "Date - YYYY-MM-DD"),
            this.generateAttribute(context, `${prefix}.time`, "time- HH:mm:ss"),
            this.generateAttribute(context, `${prefix}.time2`, "time2- HH:mm"),
            this.generateAttribute(context, `${prefix}.timestamp`, "timestamp- " + Date.now(), this.VALUE_TYPES.NUMBER),
            this.generateAttribute(context, `${prefix}.dateIso`, "Date ISOString - " + new Date().toISOString()),
            this.generateAttribute(
                context,
                `${prefix}.wakeUpCount`,
                "Automation Wake Up Count",
                this.VALUE_TYPES.NUMBER
            ),
            this.generateAttribute(
                context,
                `${prefix}.duration`,
                "Automation Duration",
                this.VALUE_TYPES.OBJECT,
                undefined,
                [
                    this.generateAttribute(
                        context,
                        `${prefix}.duration.seconds`,
                        "Duration as Seconds",
                        this.VALUE_TYPES.NUMBER
                    ),
                    this.generateAttribute(
                        context,
                        `${prefix}.duration.minutes`,
                        "Duration as Minutes",
                        this.VALUE_TYPES.NUMBER
                    ),
                    this.generateAttribute(
                        context,
                        `${prefix}.duration.hours`,
                        "Duration as Hours",
                        this.VALUE_TYPES.NUMBER
                    ),
                    this.generateAttribute(
                        context,
                        `${prefix}.duration.days`,
                        "Duration as Days",
                        this.VALUE_TYPES.NUMBER
                    ),
                ]
            ),
        ];
    }

    generateWebhookAttributes(automation) {
        const context = this.contexts.WEBHOOK;
        const body = _.get(automation, "options.webhook.example.body", {});
        let bodyFields = Object.keys(body).map((key) => {
            const value = body[key];
            const valueType = typeof value;
            const types = {
                string: this.VALUE_TYPES.STRING,
                number: this.VALUE_TYPES.NUMBER,
                boolean: this.VALUE_TYPES.BOOLEAN,
                object: this.VALUE_TYPES.OBJECT,
            };
            const type = types[valueType] || this.VALUE_TYPES.MIXED;
            return this.generateAttribute(context, `webhook.body.${key}`, key, type);
        });
        console.log("listBlockWebhookFields bodyFields", {
            bodyFields,
            body,
        });
        bodyFields = bodyFields.length ? bodyFields : undefined;

        const headers = _.get(automation, "options.webhook.example.headers", {});
        let headerFields = Object.keys(headers).map((key) => {
            const value = headers[key];
            const valueType = typeof value;
            const types = {
                string: this.VALUE_TYPES.STRING,
                number: this.VALUE_TYPES.NUMBER,
                boolean: this.VALUE_TYPES.BOOLEAN,
                object: this.VALUE_TYPES.OBJECT,
            };
            const type = types[valueType] || this.VALUE_TYPES.MIXED;
            return this.generateAttribute(context, `webhook.headers.${key}`, key, type);
        });
        headerFields = headerFields.length ? headerFields : undefined;
        console.log("listBlockWebhookFields headerFields", {
            headerFields,
            headers,
        });

        const query = _.get(automation, "options.webhook.example.query", {});
        let queryFields = Object.keys(query).map((key) => {
            const value = query[key];
            const valueType = typeof value;
            const types = {
                string: this.VALUE_TYPES.STRING,
                number: this.VALUE_TYPES.NUMBER,
                boolean: this.VALUE_TYPES.BOOLEAN,
                object: this.VALUE_TYPES.OBJECT,
            };
            const type = types[valueType] || this.VALUE_TYPES.MIXED;
            return this.generateAttribute(context, `webhook.query.${key}`, key, type);
        });
        queryFields = queryFields.length ? queryFields : undefined;
        return [
            this.generateAttribute(context, `webhook.query`, "Query", this.VALUE_TYPES.OBJECT, undefined, queryFields),
            this.generateAttribute(
                context,
                `webhook.headers`,
                "Headers",
                this.VALUE_TYPES.OBJECT,
                undefined,
                headerFields
            ),
            this.generateAttribute(context, `webhook.body`, "Body", this.VALUE_TYPES.MIXED, undefined, bodyFields),
            this.generateAttribute(context, `webhook.method`, "Method", this.VALUE_TYPES.STRING),
        ];
    }

    generateContactAttributes(prefix = "contact") {
        const context = this.contexts.CONTACT;
        prefix = prefix ? `${prefix}.` : "";
        return [
            this.generateAttribute(context, `${prefix}$..*`, "*", this.VALUE_TYPES.OBJECT),
            this.generateAttribute(context, `${prefix}_id`, "Contact ID"),
            this.generateAttribute(context, `${prefix}fullname`, "Fullname"),
            this.generateAttribute(context, `${prefix}email`, "Email"),
            this.generateAttribute(context, `${prefix}phone`, "Phone"),
            this.generateAttribute(context, `${prefix}whatsappNumber`, "Whatsapp Number"),
            this.generateAttribute(context, `${prefix}visitingPage`, "Last Visiting Page URL"),
            this.generateAttribute(context, `${prefix}os`, "Operating System"),
            this.generateAttribute(context, `${prefix}browser`, "Browser"),
            this.generateAttribute(context, `${prefix}ipAddress`, "IP Address"),
            this.generateAttribute(context, `${prefix}country`, "Country"),
            this.generateAttribute(context, `${prefix}city`, "City"),
            this.generateAttribute(context, `${prefix}platform`, "Platform"),
            this.generateAttribute(context, `${prefix}instagramUserId`, "Instagram User ID"),
            this.generateAttribute(context, `${prefix}messengerUserId`, "Messenger User ID"),
            this.generateAttribute(context, `${prefix}telegramUserId`, "Telegram User ID"),
            this.generateAttribute(context, `${prefix}metaLeadId`, "Meta Lead ID"),
            this.generateAttribute(context, `${prefix}preferredLang`, "Preferred Language"),
            this.generateAttribute(context, `${prefix}channelRefId`, "Channel Ref ID"),
            this.generateAttribute(context, `${prefix}conversationCount`, "Total Chat Count", this.VALUE_TYPES.NUMBER),
            this.generateAttribute(context, `${prefix}tags`, "Tags", this.VALUE_TYPES.ARRAY_OBJECT, undefined, [
                this.generateAttribute(context, `${prefix}tags._id`, "Tag ID", this.VALUE_TYPES.ARRAY_OBJECT),
                this.generateAttribute(context, `${prefix}tags.name`, "Tag Name"),
            ]),
            this.generateAttribute(
                context,
                `${prefix}lastConversation`,
                "Last Conversation",
                this.VALUE_TYPES.OBJECT,
                undefined,
                this.generateConversationAttributes(`${prefix}lastConversation`, false)
            ),
            this.generateAttribute(
                context,
                `${prefix}metaLeadInfo`,
                "Meta Lead Info",
                this.VALUE_TYPES.OBJECT,
                undefined,
                [
                    this.generateAttribute(context, `${prefix}metaLeadInfo.id`, "Lead ID"),
                    this.generateAttribute(context, `${prefix}metaLeadInfo.ad_id`, "Ad ID"),
                    this.generateAttribute(context, `${prefix}metaLeadInfo.ad_name`, "Ad Name"),
                    this.generateAttribute(context, `${prefix}metaLeadInfo.adset_id`, "Ad Set ID"),
                    this.generateAttribute(context, `${prefix}metaLeadInfo.adset_name`, "Ad Set Name"),
                    this.generateAttribute(context, `${prefix}metaLeadInfo.campaign_name`, "Campaign Name"),
                    this.generateAttribute(context, `${prefix}metaLeadInfo.campaign_id`, "Campaign ID"),
                    this.generateAttribute(context, `${prefix}metaLeadInfo.form_id`, "Form ID"),
                    this.generateAttribute(context, `${prefix}metaLeadInfo.form_name`, "Form Name"),
                    this.generateAttribute(context, `${prefix}metaLeadInfo.platform`, "Platform"),
                    this.generateAttribute(context, `${prefix}metaLeadInfo.created_time`, "Created Time"),
                    this.generateAttribute(
                        context,
                        `${prefix}metaLeadInfo.field_data`,
                        "Field Data",
                        this.VALUE_TYPES.ARRAY
                    ),
                ]
            ),
            this.generateAttribute(
                context,
                `${prefix}metaReferrals`,
                "Related Ads",
                this.VALUE_TYPES.ARRAY_OBJECT,
                undefined,
                [
                    this.generateAttribute(
                        context,
                        `${prefix}metaReferrals`,
                        "All Meta Referrals",
                        this.VALUE_TYPES.ARRAY_OBJECT,
                        undefined
                    ),
                    this.generateAttribute(
                        context,
                        `${prefix}metaReferrals.0`,
                        "First Ad",
                        this.VALUE_TYPES.OBJECT,
                        undefined,
                        [
                            this.generateAttribute(context, `${prefix}metaReferrals.0.sourceType`, "Source Type"),
                            this.generateAttribute(context, `${prefix}metaReferrals.0.sourceId`, "Source ID"),
                            this.generateAttribute(context, `${prefix}metaReferrals.0.sourceLink`, "Source Link"),
                            this.generateAttribute(context, `${prefix}metaReferrals.0.headline`, "Headline"),
                            this.generateAttribute(context, `${prefix}metaReferrals.0.body`, "Ad Body"),
                            this.generateAttribute(
                                context,
                                `${prefix}metaReferrals.0.createdAt`,
                                "Created At",
                                this.VALUE_TYPES.DATETIME
                            ),
                            this.generateAttribute(
                                context,
                                `${prefix}metaReferrals.0.updatedAt`,
                                "Updated At",
                                this.VALUE_TYPES.DATETIME
                            ),
                        ]
                    ),
                ]
            ),
        ];
    }

    generateUpdateContactAttributes(prefix = "contact") {
        const context = this.contexts.CONTACT;
        prefix = prefix ? `${prefix}.` : "";
        return [
            this.generateAttribute(context, `${prefix}$..*`, "*", this.VALUE_TYPES.OBJECT),
            this.generateAttribute(context, `${prefix}fullname`, "Fullname", this.VALUE_TYPES.ARRAY_OBJECT, undefined, [
                this.generateAttribute(context, `${prefix}fullname.oldValue`, "Eski Bilgi"),
                this.generateAttribute(context, `${prefix}fullname.newValue`, "Yeni Bilgi"),
                this.generateAttribute(context, `${prefix}fullname.updated`, "Güncellendi"),
            ]),
            this.generateAttribute(context, `${prefix}email`, "Email", this.VALUE_TYPES.ARRAY_OBJECT, undefined, [
                this.generateAttribute(context, `${prefix}email.oldValue`, "Eski Bilgi"),
                this.generateAttribute(context, `${prefix}email.newValue`, "Yeni Bilgi"),
                this.generateAttribute(context, `${prefix}email.updated`, "Güncellendi"),
            ]),
            this.generateAttribute(context, `${prefix}phone`, "Phone", this.VALUE_TYPES.ARRAY_OBJECT, undefined, [
                this.generateAttribute(context, `${prefix}phone.oldValue`, "Eski Bilgi"),
                this.generateAttribute(context, `${prefix}phone.newValue`, "Yeni Bilgi"),
                this.generateAttribute(context, `${prefix}phone.updated`, "Güncellendi"),
            ]),
            this.generateAttribute(
                context,
                `${prefix}whatsappNumber`,
                "Whatsapp Number",
                this.VALUE_TYPES.ARRAY_OBJECT,
                undefined,
                [
                    this.generateAttribute(context, `${prefix}whatsappNumber.oldValue`, "Eski Bilgi"),
                    this.generateAttribute(context, `${prefix}whatsappNumber.newValue`, "Yeni Bilgi"),
                    this.generateAttribute(context, `${prefix}whatsappNumber.updated`, "Güncellendi"),
                ]
            ),
            this.generateAttribute(
                context,
                `${prefix}metaReferrals`,
                "Meta Referrals",
                this.VALUE_TYPES.ARRAY_OBJECT,
                undefined,
                [
                    this.generateAttribute(context, `${prefix}metaReferrals.oldValue`, "Eski Bilgi"),
                    this.generateAttribute(context, `${prefix}metaReferrals.newValue`, "Yeni Bilgi"),
                    this.generateAttribute(context, `${prefix}metaReferrals.updated`, "Güncellendi"),
                ]
            ),
            this.generateAttribute(
                context,
                `${prefix}preferredAgents`,
                "Preferred Agents",
                this.VALUE_TYPES.ARRAY_OBJECT,
                undefined,
                [
                    this.generateAttribute(context, `${prefix}preferredAgents.oldValue`, "Eski Bilgi"),
                    this.generateAttribute(context, `${prefix}preferredAgents.newValue`, "Yeni Bilgi"),
                    this.generateAttribute(context, `${prefix}preferredAgents.updated`, "Güncellendi"),
                ]
            ),
            this.generateAttribute(context, `${prefix}tags`, "Tags", this.VALUE_TYPES.ARRAY_OBJECT, undefined, [
                this.generateAttribute(context, `${prefix}tags.oldValue`, "Eski Bilgi"),
                this.generateAttribute(context, `${prefix}tags.newValue`, "Yeni Bilgi"),
                this.generateAttribute(context, `${prefix}tags.updated`, "Güncellendi"),
            ]),
            this.generateAttribute(
                context,
                `${prefix}preferredLang`,
                "Preferred Lang",
                this.VALUE_TYPES.ARRAY_OBJECT,
                undefined,
                [
                    this.generateAttribute(context, `${prefix}preferredLang.oldValue`, "Eski Bilgi"),
                    this.generateAttribute(context, `${prefix}preferredLang.newValue`, "Yeni Bilgi"),
                    this.generateAttribute(context, `${prefix}preferredLang.updated`, "Güncellendi"),
                ]
            ),
            this.generateAttribute(
                context,
                `${prefix}telegramInfo`,
                "Telegram Info",
                this.VALUE_TYPES.ARRAY_OBJECT,
                undefined,
                [
                    this.generateAttribute(context, `${prefix}telegramInfo.oldValue`, "Eski Bilgi"),
                    this.generateAttribute(context, `${prefix}telegramInfo.newValue`, "Yeni Bilgi"),
                    this.generateAttribute(context, `${prefix}telegramInfo.updated`, "Güncellendi"),
                ]
            ),
            this.generateAttribute(
                context,
                `${prefix}metaLeadInfo`,
                "MetaLead Info",
                this.VALUE_TYPES.ARRAY_OBJECT,
                undefined,
                [
                    this.generateAttribute(context, `${prefix}metaLeadInfo.oldValue`, "Eski Bilgi"),
                    this.generateAttribute(context, `${prefix}metaLeadInfo.newValue`, "Yeni Bilgi"),
                    this.generateAttribute(context, `${prefix}metaLeadInfo.updated`, "Güncellendi"),
                ]
            ),
            this.generateAttribute(
                context,
                `${prefix}whatsappInfo`,
                "Whatsapp Info",
                this.VALUE_TYPES.ARRAY_OBJECT,
                undefined,
                [
                    this.generateAttribute(context, `${prefix}whatsappInfo.oldValue`, "Eski Bilgi"),
                    this.generateAttribute(context, `${prefix}whatsappInfo.newValue`, "Yeni Bilgi"),
                    this.generateAttribute(context, `${prefix}whatsappInfo.updated`, "Güncellendi"),
                ]
            ),
        ];
    }

    generateMessageAttributes(context, prefix = "") {
        prefix = prefix ? `${prefix}.` : "";

        return [
            this.generateAttribute(context, `${prefix}siteId`, "Site ID"),
            this.generateAttribute(context, `${prefix}description`, "Message Text"),
            this.generateAttribute(context, `${prefix}baseLang`, "Base Language"),
            this.generateAttribute(context, `${prefix}isPrivate`, "Is Private Message", this.VALUE_TYPES.BOOLEAN),
            this.generateAttribute(context, `${prefix}seen`, "Seen", this.VALUE_TYPES.BOOLEAN),
            this.generateAttribute(context, `${prefix}fromBroadcast`, "From Broadcast", this.VALUE_TYPES.BOOLEAN),
            this.generateAttribute(context, `${prefix}fromAPI`, "From API", this.VALUE_TYPES.BOOLEAN),
            this.generateAttribute(context, `${prefix}fromAutomation`, "From Automation", this.VALUE_TYPES.BOOLEAN),
            this.generateAttribute(context, `${prefix}isDeleted`, "Is Deleted", this.VALUE_TYPES.BOOLEAN),
            this.generateAttribute(context, `${prefix}createdDate`, "Created Date", this.VALUE_TYPES.DATETIME),
            this.generateAttribute(context, `${prefix}uniqMessageId`, "Unique Message ID"),
            this.generateAttribute(context, `${prefix}metaResponseType`, "Meta Response Type"),
            this.generateAttribute(context, `${prefix}messageType`, "Message Type"),
            this.generateAttribute(context, `${prefix}systemMessageType`, "System Message Type"),
            this.generateAttribute(context, `${prefix}payload`, "Payload", this.VALUE_TYPES.OBJECT),
            this.generateAttribute(
                context,
                `${prefix}lastClickedButton`,
                "Last Clicked Button",
                this.VALUE_TYPES.OBJECT
            ),
            this.generateAttribute(context, `${prefix}whatsappBusinessMessageId`, "Whatsapp Business Message ID"),
            this.generateAttribute(context, `${prefix}gupshupMessageId`, "Gupshup Message ID"),
            this.generateAttribute(context, `${prefix}metaMessageId`, "Meta Message ID"),
            this.generateAttribute(
                context,
                `${prefix}lastWhatsappBusinessStatus`,
                "Last Whatsapp Business Status",
                this.VALUE_TYPES.OBJECT,
                undefined,
                [
                    this.generateAttribute(context, `${prefix}lastWhatsappBusinessStatus.status`, "Status"),
                    this.generateAttribute(context, `${prefix}lastWhatsappBusinessStatus.reason`, "Reason"),
                ]
            ),
            this.generateAttribute(context, `${prefix}contact`, "Contact", this.VALUE_TYPES.OBJECT, undefined, [
                ...this.generateContactAttributes(`${prefix}contact`),
            ]),
            //todo add assset
            //todo add user
            //todo add location
            //todo add metaPost
        ];
    }

    generateEmailAttributes(context, prefix = "") {
        prefix = prefix ? `${prefix}.` : "";

        return [
            this.generateAttribute(context, `${prefix}siteId`, "Site ID"),
            this.generateAttribute(context, `${prefix}messageId`, "Message ID"),
            this.generateAttribute(context, `${prefix}incoming`, "Incoming", this.VALUE_TYPES.BOOLEAN),
            this.generateAttribute(context, `${prefix}sender`, "Sender Email Address"),
            this.generateAttribute(context, `${prefix}senderUserId`, "Sender User Id"),
            this.generateAttribute(context, `${prefix}to_list`, "To Email Address List", this.VALUE_TYPES.ARRAY_STRING),
            this.generateAttribute(context, `${prefix}cc_list`, "CC Email Address List", this.VALUE_TYPES.ARRAY_STRING),
            this.generateAttribute(
                context,
                `${prefix}bcc_list`,
                "BCC Email Address List",
                this.VALUE_TYPES.ARRAY_STRING
            ),
            this.generateAttribute(context, `${prefix}subject`, "Subject"),
            this.generateAttribute(context, `${prefix}body_text`, "Body Text"),
            this.generateAttribute(context, `${prefix}body_html`, "Body HTML"),
            this.generateAttribute(context, `${prefix}created_at`, "Created Date", this.VALUE_TYPES.DATETIME),
            this.generateAttribute(context, `${prefix}deleted`, "Deleted", this.VALUE_TYPES.BOOLEAN),
            this.generateAttribute(context, `${prefix}replay_to_message_id`, "Replay To Message ID"),
            this.generateAttribute(context, `${prefix}thread_id`, "Thread ID"),
            this.generateAttribute(
                context,
                `${prefix}refContact`,
                "Reference Contact",
                this.VALUE_TYPES.OBJECT,
                undefined,
                [...this.generateContactAttributes(`${prefix}refContact`)]
            ),
            this.generateAttribute(
                context,
                `${prefix}ref_department_ids`,
                "Reference Department IDS",
                this.VALUE_TYPES.ARRAY
            ),
            this.generateAttribute(context, `${prefix}is_ticket`, "Is Ticket", this.VALUE_TYPES.BOOLEAN),
            this.generateAttribute(context, `${prefix}attachments`, "Attachments", this.VALUE_TYPES.ARRAY_OBJECT),
        ];
    }

    generateConversationAttributes(prefix = "conversation", includeContact = true) {
        const context = this.contexts.CONVERSATION;

        return [
            this.generateAttribute(context, `${prefix}._id`, "Chat ID"),
            this.generateAttribute(context, `${prefix}.siteId`, "Site ID"),
            this.generateAttribute(context, `${prefix}.channel`, "Channel"),
            this.generateAttribute(context, `${prefix}.languageCode`, "Language Code"),
            this.generateAttribute(context, `${prefix}.createdDate`, "Created Date", this.VALUE_TYPES.DATETIME),
            this.generateAttribute(context, `${prefix}.destroyedDate`, "Destroyed Date", this.VALUE_TYPES.DATETIME),
            this.generateAttribute(context, `${prefix}.connectedDate`, "Connected Date", this.VALUE_TYPES.DATETIME),
            this.generateAttribute(
                context,
                `${prefix}.agentWaitStartDate`,
                "Agent Wait Start Date",
                this.VALUE_TYPES.DATETIME
            ),
            this.generateAttribute(
                context,
                `${prefix}.firstMessageDate`,
                "Agent First Answer Date ",
                this.VALUE_TYPES.DATETIME
            ),
            this.generateAttribute(
                context,
                `${prefix}.firstVisitorMessageDate`,
                "Contact First Message Date ",
                this.VALUE_TYPES.DATETIME
            ),
            this.generateAttribute(context, `${prefix}.ipAddress`, "IP Address"),
            this.generateAttribute(context, `${prefix}.subject`, "Department"),
            this.generateAttribute(context, `${prefix}.point`, "Point"),
            this.generateAttribute(
                context,
                `${prefix}.ref`,
                "Reference Platform",
                this.VALUE_TYPES.STRING,
                "select",
                undefined,
                undefined,
                undefined,
                undefined,
                [
                    //["whatsapp", "instagram", "telegram", "web", "messenger"]
                    { value: "whatsapp", label: "Whatsapp" },
                    { value: "instagram", label: "Instagram" },
                    { value: "telegram", label: "Telegram" },
                    { value: "web", label: "Web" },
                    { value: "messenger", label: "Messenger" },
                ]
            ),
            this.generateAttribute(
                context,
                `${prefix}.status`,
                "Status",
                this.VALUE_TYPES.STRING,
                "select",
                undefined,
                undefined,
                undefined,
                undefined,
                [
                    //"chatbot", "new", "active", "lost", "destroyed", "private"
                    { value: "chatbot", label: "Currently chatting with chatbot" },
                    { value: "new", label: "New Chat waiting live agent" },
                    { value: "active", label: "Chatting with live agent" },
                    { value: "lost", label: "Agent Lost waiting for agent" },
                    { value: "destroyed", label: "Chat Ended" },
                    { value: "private", label: "Private Chat" },
                ]
            ),
            this.generateAttribute(
                context,
                `${prefix}.startedFrom`,
                "Started From",
                this.VALUE_TYPES.STRING,
                "select",
                undefined,
                undefined,
                undefined,
                undefined,
                [
                    { value: "user", label: "Started From an Agent" },
                    { value: "visitor", label: "Started From Contact" },
                    { value: "api", label: "Started From API" },
                    { value: "broadcast", label: "Started From Broadcast" },
                    { value: "instagram-comment", label: "Started From Instagram Comment" },
                    { value: "page-comment", label: "Started From Page Comment" },
                    { value: "instagram-mention", label: "Started From Instagram Mention" },
                    { value: "automation", label: "Started From Automation" },
                ]
            ),
            this.generateAttribute(
                context,
                `${prefix}.handledBy`,
                "Chat Handled By",
                this.VALUE_TYPES.STRING,
                "select",
                undefined,
                undefined,
                undefined,
                undefined,
                [
                    /*"chatbot", "live", "hybrid", "none"*/
                    { value: "chatbot", label: "Handled by Chatbot" },
                    { value: "live", label: "Handled by Live Agent" },
                    { value: "hybrid", label: "Handled by Hybrid (Agent + Chatbot)" },
                    { value: "none", label: "Not Handled" },
                ]
            ),
            this.generateAttribute(context, `${prefix}.fullname`, "Contact Fullname (in chat session)"),
            this.generateAttribute(context, `${prefix}.email`, "Contact Email (in chat session)"),
            this.generateAttribute(context, `${prefix}.languageCode`, "Chat Language"),
            this.generateAttribute(context, `${prefix}.userAgent`, "Web Contact User Agent String"),
            this.generateAttribute(context, `${prefix}.users`, "Agents in Chat"),
            this.generateAttribute(
                context,
                `${prefix}.destroyedByChatBot`,
                "Destroyed By Chatbot",
                this.VALUE_TYPES.BOOLEAN
            ),
            this.generateAttribute(
                context,
                `${prefix}.destroyedByVisitor`,
                "Destroyed By Contact",
                this.VALUE_TYPES.BOOLEAN
            ),
            this.generateAttribute(
                context,
                `${prefix}.startedWithChatBot`,
                "Started With Chatbot",
                this.VALUE_TYPES.BOOLEAN
            ),
            this.generateAttribute(context, `${prefix}.videoCall`, "Video Call", this.VALUE_TYPES.BOOLEAN),
            this.generateAttribute(context, `${prefix}.isLost`, "Is Lost Chat", this.VALUE_TYPES.BOOLEAN),
            this.generateAttribute(context, `${prefix}.isMissed`, "Is Missed Chat", this.VALUE_TYPES.BOOLEAN),
            this.generateAttribute(context, `${prefix}.lastConnectedUser`, "Last Connected User"),
            this.generateAttribute(context, `${prefix}.connectedUser`, "First Connected User"),
            this.generateAttribute(context, `${prefix}.homeSite`, "Web Site Origin"),
            this.generateAttribute(context, `${prefix}.visitingPage`, "Web Contact Visiting Page URL"),
            this.generateAttribute(context, `${prefix}.whatsappNumber`, "Whatsapp Number"),
            this.generateAttribute(context, `${prefix}.inactive`, "Inactive", this.VALUE_TYPES.BOOLEAN),
            this.generateAttribute(context, `${prefix}.tags`, "Tags", this.VALUE_TYPES.ARRAY_STRING),
            this.generateAttribute(context, `${prefix}.instagramUserId`, "Instagram User ID"),
            this.generateAttribute(context, `${prefix}.messengerUserId`, "Messenger User ID"),
            this.generateAttribute(context, `${prefix}.telegramUserId`, "Telegram User ID"),
            this.generateAttribute(context, `${prefix}.channel`, "Channel"),
            this.generateAttribute(
                context,
                `${prefix}.duration`,
                "Chat Session Duration (ms)",
                this.VALUE_TYPES.NUMBER
            ),
            this.generateAttribute(
                context,
                `${prefix}.agentWaitTime`,
                "Agent Wait Duration (ms)",
                this.VALUE_TYPES.NUMBER
            ),

            includeContact
                ? this.generateAttribute(
                      context,
                      `${prefix}.contact`,
                      "Contact",
                      this.VALUE_TYPES.OBJECT,
                      undefined,
                      this.generateContactAttributes(`${prefix}.contact`)
                  )
                : false,
        ].filter(Boolean);
    }

    generateEvents(automation) {
        if (automation.options.trigger === "event") {
            const chatEvents = this.generateChatModuleEventAttributes();
            const contactEvents = this.generateContactModuleEventAttributes();
            const tagEvents = this.generateTagModuleEventAttributes();
            return [...chatEvents, ...contactEvents, ...tagEvents].filter(
                (x) => x.value === `event.${automation.options.event.type}`
            );
        } else {
            return [];
        }
    }

    generateChatModuleEventAttributes() {
        //event: AutomationEventTypes.CHAT.CREATED
        //event: AutomationEventTypes.CHAT.CLOSED
        return [
            this.generateAttribute(
                this.contexts.EVENT,
                `event.${AutomationEventTypes.CHAT.CREATED}`,
                "Chat Created",
                this.VALUE_TYPES.OBJECT,
                undefined,
                [
                    this.generateAttribute(
                        this.contexts.EVENT,
                        `event.${AutomationEventTypes.CHAT.CREATED}.conversation`,
                        "Created Conversation",
                        this.VALUE_TYPES.OBJECT,
                        undefined,
                        this.generateConversationAttributes(`event.${AutomationEventTypes.CHAT.CREATED}.conversation`)
                    ),
                ]
            ),
            this.generateAttribute(
                this.contexts.EVENT,
                `event.${AutomationEventTypes.CHAT.CLOSED}`,
                "Chat Closed",
                this.VALUE_TYPES.OBJECT,
                undefined,
                [
                    this.generateAttribute(
                        this.contexts.EVENT,
                        `event.${AutomationEventTypes.CHAT.CLOSED}.conversation`,
                        "Closed Conversation",
                        this.VALUE_TYPES.OBJECT,
                        undefined,
                        this.generateConversationAttributes(`event.${AutomationEventTypes.CHAT.CLOSED}.conversation`)
                    ),
                ]
            ),
        ];
    }

    generateContactModuleEventAttributes() {
        //event: AutomationEventTypes.CONTACT.CREATED
        //event: AutomationEventTypes.CONTACT.UPDATE
        return [
            this.generateAttribute(
                this.contexts.EVENT,
                `event.${AutomationEventTypes.CONTACT.CREATED}`,
                "New Contact Created",
                this.VALUE_TYPES.OBJECT,
                undefined,
                [
                    this.generateAttribute(
                        this.contexts.EVENT,
                        `event.${AutomationEventTypes.CONTACT.CREATED}.contact`,
                        "Created Contact",
                        this.VALUE_TYPES.OBJECT,
                        undefined,
                        this.generateContactAttributes(`event.${AutomationEventTypes.CONTACT.CREATED}.contact`)
                    ),
                ]
            ),
            this.generateAttribute(
                this.contexts.EVENT,
                `event.${AutomationEventTypes.CONTACT.UPDATED}`,
                "Contact Updated",
                this.VALUE_TYPES.OBJECT,
                undefined,
                [
                    this.generateAttribute(
                        this.contexts.EVENT,
                        `event.${AutomationEventTypes.CONTACT.UPDATED}.updatedContact`,
                        "Updated Contact",
                        this.VALUE_TYPES.OBJECT,
                        undefined,
                        this.generateContactAttributes(`event.${AutomationEventTypes.CONTACT.UPDATED}.updatedContact`)
                    ),
                    this.generateAttribute(
                        this.contexts.EVENT,
                        `event.${AutomationEventTypes.CONTACT.UPDATED}.updatedFields`,
                        "Updated Fields",
                        this.VALUE_TYPES.OBJECT,
                        undefined,
                        this.generateUpdateContactAttributes(
                            `event.${AutomationEventTypes.CONTACT.UPDATED}.updatedFields`
                        )
                    ),
                ]
            ),
        ];
    }

    generateUpdateTagAttributes(prefix = "contact") {
        const context = this.contexts.EVENT;
        prefix = prefix ? `${prefix}.` : "";
        return [
            this.generateAttribute(context, `${prefix}tagId`, "Tag ID"),
            this.generateAttribute(context, `${prefix}tagName`, "Tag Name"),
        ];
    }

    generateTagModuleEventAttributes() {
        //event: AutomationEventTypes.TAG.ADDED
        //event: AutomationEventTypes.TAG.REMOVED
        return [
            this.generateAttribute(
                this.contexts.EVENT,
                `event.${AutomationEventTypes.TAG.ADDED}`,
                "Added Tag",
                this.VALUE_TYPES.OBJECT,
                undefined,
                [
                    this.generateAttribute(
                        this.contexts.EVENT,
                        `event.${AutomationEventTypes.TAG.ADDED}.updatedContact`,
                        "Updated Contact",
                        this.VALUE_TYPES.OBJECT,
                        undefined,
                        this.generateContactAttributes(`event.${AutomationEventTypes.TAG.ADDED}.updatedContact`)
                    ),
                    this.generateAttribute(
                        this.contexts.EVENT,
                        `event.${AutomationEventTypes.TAG.ADDED}.updatedFields`,
                        "Added Tag",
                        this.VALUE_TYPES.OBJECT,
                        undefined,
                        this.generateUpdateTagAttributes(`event.${AutomationEventTypes.TAG.ADDED}.updatedFields`)
                    ),
                ]
            ),
            this.generateAttribute(
                this.contexts.EVENT,
                `event.${AutomationEventTypes.TAG.REMOVED}`,
                "Removed Tag",
                this.VALUE_TYPES.OBJECT,
                undefined,
                [
                    this.generateAttribute(
                        this.contexts.EVENT,
                        `event.${AutomationEventTypes.TAG.REMOVED}.updatedContact`,
                        "Updated Contact",
                        this.VALUE_TYPES.OBJECT,
                        undefined,
                        this.generateContactAttributes(`event.${AutomationEventTypes.TAG.REMOVED}.updatedContact`)
                    ),
                    this.generateAttribute(
                        this.contexts.EVENT,
                        `event.${AutomationEventTypes.TAG.REMOVED}.updatedFields`,
                        "Removed Tag",
                        this.VALUE_TYPES.OBJECT,
                        undefined,
                        this.generateUpdateTagAttributes(`event.${AutomationEventTypes.TAG.REMOVED}.updatedFields`)
                    ),
                ]
            ),
        ];
    }

    /**

     * @return {{defaultFilterValue: string, controlType: string, availableOperators: string[], defaultOperator: string, context: string, name: string, options: {}, label: string, value: string, subAttributes: *[]}}
     */

    generateAttribute(
        context,
        value,
        label,
        valueType = "string",
        controlType = "input",
        subAttributes = undefined,
        defaultFilterValue = undefined,
        defaultOperator = undefined,
        availableOperators = undefined,
        options = undefined
    ) {
        return {
            context,
            name,
            value,
            label,
            defaultOperator: defaultOperator || "equal",
            defaultFilterValue,
            availableOperators: availableOperators || this.defaultOperators[valueType] || ["equal", "not_equal"],
            controlType,
            options,
            subAttributes,
            valueType,
        };
    }
}
